import { NavLink, Stack } from "@mantine/core";
import { type RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import railClasses from "@mountayaapp/theme/railNavLink.module.css";

import { colorForMethod } from "./httpColors";
import classes from "./redocNav.module.css";
import type { SpecSection } from "./sections";

/**
 * RedocNavProps carry the outline read off the rendered reference and a ref to
 * the element that owns the reference's vertical scroll. The rail needs that
 * element as the observation root: the reference scrolls inside its own box
 * rather than the document, so an observer left on the viewport would never see
 * a section cross.
 */
interface RedocNavProps {
  scrollRef: RefObject<HTMLDivElement | null>;
  sections: SpecSection[];
}

/**
 * useActiveSection tracks which operation is currently being read, so the rail
 * doubles as a position indicator through a long reference. It observes every
 * operation against the reference's own scroll box, narrowing the band to the top
 * of that box, and takes the first entry in document order sitting inside it. The
 * previous value is held whenever nothing intersects, so the highlight never
 * blanks out between operations.
 */
const useActiveSection = (
  sections: SpecSection[],
  scrollRef: RefObject<HTMLDivElement | null>,
): string | null => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const root = scrollRef.current;
    const ordered = sections.flatMap((section) => section.operations.map((entry) => entry.id));

    if (!root || ordered.length === 0) {
      return;
    }

    const elements = ordered
      .map((id) => root.querySelector(`[data-section-id="${CSS.escape(id)}"]`))
      .filter((element): element is Element => element !== null);

    if (elements.length === 0) {
      return;
    }

    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          const id = record.target.getAttribute("data-section-id") ?? "";

          if (record.isIntersecting) {
            visible.add(id);
          } else {
            visible.delete(id);
          }
        }

        const firstVisible = ordered.find((id) => visible.has(id));

        if (firstVisible) {
          setActiveId(firstVisible);
        }
      },
      { root, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );

    for (const element of elements) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [sections, scrollRef]);

  return activeId;
};

/**
 * activeGroupId returns the id of the tag group holding the operation being read,
 * or null before any operation has been reached.
 */
const activeGroupId = (sections: SpecSection[], activeId: string | null): string | null =>
  sections.find((section) => section.operations.some((entry) => entry.id === activeId))?.id ?? null;

/**
 * scrollToSection brings a section to the top of the reference's scroll box and
 * records it in the address bar, so an operation reached from the rail can be
 * linked to and reloaded. The URL is replaced rather than assigned: Redoc listens
 * for hash changes and would scroll a second time on its own, landing the reader
 * in the same place twice over.
 */
const scrollToSection = (root: HTMLDivElement | null, id: string): void => {
  const target = root?.querySelector(`[data-section-id="${CSS.escape(id)}"]`);

  if (!target) {
    return;
  }

  target.scrollIntoView({ block: "start" });
  window.history.replaceState(null, "", `#${id}`);
};

/**
 * RedocNav is the OpenAPI reference's navigation rail: one collapsible group per
 * tag, each listing its operations behind their HTTP method badge. It is the
 * documentation sidebar applied to a specification — it reuses that rail's own
 * section, item and guide-rule classes, so a reader moving between a guide and
 * the reference meets one navigation treatment — and it is what gives these pages
 * navigation at all, since Redoc's built-in menu is collapsed in favour of it.
 *
 * The group holding the operation being read opens itself, and that operation
 * carries the rail's active treatment, so the reader's position in a long
 * specification is always visible without hunting for it.
 */
export const RedocNav = ({ scrollRef, sections }: RedocNavProps) => {
  const { t } = useTranslation("docs");
  const activeId = useActiveSection(sections, scrollRef);

  // Each group follows the reader by default, opening as they scroll into it, so
  // the rail keeps pace through a long reference. A group the reader opens or
  // closes themselves is remembered here and stops following, since a disclosure
  // that reopens itself after being dismissed reads as broken.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  if (sections.length === 0) {
    return null;
  }

  // At the top of the reference the reader is still on the Hero and the tag
  // heading, so no operation has crossed into the observed band yet and nothing
  // is active. The first group stands in until one does, so the rail opens with
  // its first operations showing rather than as a column of closed headings.
  const openGroupId = activeGroupId(sections, activeId) ?? sections[0]?.id;

  return (
    <nav aria-label={t("nav.specSections")} className={`${railClasses.rail} ${classes.nav}`}>
      <Stack gap="md">
        {sections.map((section) => (
          <NavLink
            aria-label={t("nav.toggleGroup", { group: section.label })}
            childrenOffset={0}
            classNames={{ children: railClasses.children, root: railClasses.section }}
            fw={600}
            key={section.id}
            label={section.label}
            onChange={(opened) => setOverrides((current) => ({ ...current, [section.id]: opened }))}
            opened={overrides[section.id] ?? section.id === openGroupId}
          >
            {section.operations.map((operation) => (
              <NavLink
                active={operation.id === activeId}
                classNames={{ root: railClasses.item }}
                component="a"
                href={`#${operation.id}`}
                key={operation.id}
                label={<span className={classes.label}>{operation.label}</span>}
                leftSection={
                  <span
                    className={classes.badge}
                    style={{ backgroundColor: colorForMethod(operation.method) }}
                  >
                    {operation.method}
                  </span>
                }
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection(scrollRef.current, operation.id);
                }}
              />
            ))}
          </NavLink>
        ))}
      </Stack>
    </nav>
  );
};
